import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import { Checkbox } from '../components/ui/checkbox';
import { useTenantSettings } from '../hooks/useTenantSettings';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Upload, Loader2, Check, AlertTriangle, Clock, FileText, ChevronDown, ChevronUp, Tag, Layers } from 'lucide-react';
import { format } from 'date-fns';

const NONE = '__none__';

export default function PayrollExport({ api, platform, platformName, connectionId }) {
  const { fmtDateShort } = useTenantSettings();
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState(new Set());
  const [expandedWorkers, setExpandedWorkers] = useState(new Set());
  const [trackingCategories, setTrackingCategories] = useState([]);
  const [trackingItemId, setTrackingItemId] = useState('');
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingSaving, setTrackingSaving] = useState(false);

  // Fetch Xero tracking categories on mount (Xero only)
  useEffect(() => {
    if (platform !== 'xero') return;
    const connQ = connectionId ? `?connection_id=${connectionId}` : '';
    setTrackingLoading(true);
    Promise.all([
      api.get(`/payroll/xero/tracking-categories${connQ}`).catch((err) => {
        console.warn('Tracking categories fetch failed:', err?.response?.status, err?.response?.data?.detail || err.message);
        return { data: { tracking_categories: [] } };
      }),
      api.get(connectionId ? `/payroll/rate-mapping?platform=xero&connection_id=${connectionId}` : '/payroll/rate-mapping').catch(() => ({ data: {} })),
    ]).then(([trackRes, mapRes]) => {
      const allOptions = [];
      for (const cat of (trackRes.data?.tracking_categories || [])) {
        for (const opt of (cat.options || [])) {
          if (opt.status === 'ACTIVE') {
            allOptions.push({ id: opt.id, name: `${cat.name}: ${opt.name}` });
          }
        }
      }
      setTrackingCategories(allOptions);
      const existing = connectionId ? (mapRes.data || {}) : ((mapRes.data?.xero) || {});
      setTrackingItemId(existing.xero_tracking_item_id || '');
    }).finally(() => setTrackingLoading(false));
  }, [api, platform, connectionId]);

  const handlePreview = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('Select a date range');
      return;
    }
    setLoading(true);
    setPreview(null);
    setExportResult(null);
    setSelectedWorkers(new Set());
    try {
      const res = await api.post('/payroll/export-preview', {
        platform,
        connection_id: connectionId || null,
        period_start: periodStart,
        period_end: periodEnd,
      });
      setPreview(res.data);
      // Select all workers by default
      const allIds = new Set((res.data.worker_totals || []).map(wt => wt.worker_id));
      setSelectedWorkers(allIds);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleWorker = (workerId) => {
    setSelectedWorkers(prev => {
      const next = new Set(prev);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview?.worker_totals) return;
    const allIds = preview.worker_totals.map(wt => wt.worker_id);
    if (selectedWorkers.size === allIds.length) {
      setSelectedWorkers(new Set());
    } else {
      setSelectedWorkers(new Set(allIds));
    }
  };

  const toggleWorkerExpand = (workerId) => {
    setExpandedWorkers(prev => {
      const next = new Set(prev);
      if (next.has(workerId)) next.delete(workerId);
      else next.add(workerId);
      return next;
    });
  };

  const handleExport = async () => {
    if (!periodStart || !periodEnd) return;
    if (selectedWorkers.size === 0) {
      toast.error('Select at least one worker to export');
      return;
    }
    if (platform === 'xero' && trackingCategories.length > 0 && !trackingItemId) {
      toast.error('Please select a Xero Tracking Category before exporting. Your Xero organisation requires it on every timesheet line.');
      return;
    }
    setExporting(true);
    setExportResult(null);
    try {
      const res = await api.post('/payroll/export-timesheets', {
        platform,
        connection_id: connectionId || null,
        period_start: periodStart,
        period_end: periodEnd,
        worker_ids: Array.from(selectedWorkers),
      });
      setExportResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const saveTrackingItem = async (value) => {
    const newVal = value === NONE ? '' : value;
    setTrackingItemId(newVal);
    setTrackingSaving(true);
    try {
      // Fetch existing mapping first to preserve other fields
      const mapRes = await api.get(connectionId ? `/payroll/rate-mapping?platform=xero&connection_id=${connectionId}` : '/payroll/rate-mapping').catch(() => ({ data: {} }));
      const existing = connectionId ? (mapRes.data || {}) : ((mapRes.data?.xero) || {});
      await api.post('/payroll/rate-mapping', {
        platform: 'xero',
        connection_id: connectionId || null,
        ordinary: existing.ordinary || null,
        overtime_1_5: existing.overtime_1_5 || null,
        overtime_2_0: existing.overtime_2_0 || null,
        travel: existing.travel || null,
        allowance_mappings: existing.allowance_mappings || {},
        xero_tracking_item_id: newVal || null,
      });
      toast.success('Tracking category saved');
    } catch (err) {
      toast.error('Failed to save tracking category');
    } finally {
      setTrackingSaving(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/payroll/export-history?limit=10');
      setHistory(res.data.exports || []);
      setShowHistory(true);
    } catch {
      toast.error('Failed to load export history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const selectedCount = selectedWorkers.size;
  const totalWorkers = preview?.worker_totals?.length || 0;
  const allSelected = totalWorkers > 0 && selectedCount === totalWorkers;

  return (
    <Card className="card-industrial">
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Export Timesheets to {platformName}
        </CardTitle>
        <CardDescription>Export approved timesheets for a pay period to {platformName} payroll</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Date range */}
        <div className="flex gap-4 items-end flex-wrap">
          <div className="space-y-1">
            <Label htmlFor={`${platform}-start`} className="text-xs">Period Start</Label>
            <DatePicker
              value={periodStart}
              onChange={setPeriodStart}
              formatDisplay={fmtDateShort}
              placeholder="Period Start"
              className="w-[180px]"
              testId={`${platform}-period-start`}
              captionLayout="dropdown"
              fromYear={new Date().getFullYear() - 5}
              toYear={new Date().getFullYear() + 1}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${platform}-end`} className="text-xs">Period End</Label>
            <DatePicker
              value={periodEnd}
              onChange={setPeriodEnd}
              formatDisplay={fmtDateShort}
              placeholder="Period End"
              minDate={periodStart}
              className="w-[180px]"
              testId={`${platform}-period-end`}
              captionLayout="dropdown"
              fromYear={new Date().getFullYear() - 5}
              toYear={new Date().getFullYear() + 1}
            />
          </div>
          <Button onClick={handlePreview} disabled={loading || !periodStart || !periodEnd} variant="outline" data-testid={`${platform}-preview-btn`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
            Preview
          </Button>
        </div>

        {/* Xero Tracking Category — shown when the org has tracking enabled */}
        {platform === 'xero' && !trackingLoading && trackingCategories.length > 0 && (
          <div className="p-3 rounded-lg border border-indigo-500/20 bg-indigo-500/5 space-y-2">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-indigo-500" />
              <Label className="text-sm font-medium">Xero Tracking Category</Label>
              {!trackingItemId && (
                <Badge variant="outline" className="border-amber-500/50 text-amber-600 text-[10px]">Required</Badge>
              )}
              {trackingItemId && (
                <Badge variant="outline" className="border-green-500/50 text-green-600 text-[10px]">Configured</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Your Xero organisation requires a Tracking Category on every timesheet line. Select the default tracking option to apply.
            </p>
            <div className="flex items-center gap-2">
              <Select value={trackingItemId || NONE} onValueChange={saveTrackingItem} disabled={trackingSaving}>
                <SelectTrigger className="w-[300px]" data-testid="export-xero-tracking-item">
                  <SelectValue placeholder="Select tracking item..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Not mapped</SelectItem>
                  {trackingCategories.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {trackingSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
          </div>
        )}

        {/* Warnings */}
        {preview?.warnings?.length > 0 && (
          <div className="space-y-1">
            {preview.warnings.map((w, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-amber-400 p-2 bg-amber-500/10 rounded border border-amber-500/20">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                {w}
              </div>
            ))}
          </div>
        )}

        {/* Preview — worker totals with selection */}
        {preview && preview.worker_totals?.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge variant="outline">{selectedCount} of {totalWorkers} workers selected</Badge>
              <Badge variant="outline">{preview.total_entries} day entries</Badge>
              <Badge variant="outline" className="text-muted-foreground">Method: {preview.calc_method === 'actual_hours' ? 'Actual Hours' : 'Docket Hours'}</Badge>
              {preview.worker_totals.some(wt => wt.block_count > 0) && (
                <Badge variant="outline" className="text-blue-400 border-blue-400/30">
                  <Layers className="h-3 w-3 mr-1" />
                  {preview.worker_totals.reduce((s, wt) => s + (wt.block_count || 0), 0)} blocks
                </Badge>
              )}
            </div>

            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Select all workers"
                        data-testid="export-select-all"
                      />
                    </TableHead>
                    <TableHead className="text-xs">Worker</TableHead>
                    <TableHead className="text-xs text-right">Days</TableHead>
                    <TableHead className="text-xs text-right">Ordinary</TableHead>
                    <TableHead className="text-xs text-right">OT 1.5x</TableHead>
                    <TableHead className="text-xs text-right">OT 2.0x</TableHead>
                    <TableHead className="text-xs text-right font-semibold">Total</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.worker_totals.map((wt, i) => {
                    const isSelected = selectedWorkers.has(wt.worker_id);
                    const isExpanded = expandedWorkers.has(wt.worker_id);
                    const workerDays = (preview.preview || []).filter(p => p.worker_id === wt.worker_id);
                    const hasBlocks = wt.block_count > 0;
                    return (
                      <>
                        <TableRow
                          key={`worker-${i}`}
                          className={`cursor-pointer transition-colors ${isSelected ? '' : 'opacity-40'}`}
                          onClick={() => toggleWorker(wt.worker_id)}
                          data-testid={`export-worker-row-${i}`}
                        >
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleWorker(wt.worker_id)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select ${wt.worker_name}`}
                              data-testid={`export-worker-check-${i}`}
                            />
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            {wt.worker_name}
                            {hasBlocks && (
                              <Badge variant="outline" className="ml-2 text-[10px] text-blue-400 border-blue-400/30">{wt.block_count} blocks</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-right">{wt.days}</TableCell>
                          <TableCell className="text-sm text-right">{wt.ordinary.toFixed(1)}h</TableCell>
                          <TableCell className="text-sm text-right">{wt.overtime_1_5 > 0 ? `${wt.overtime_1_5.toFixed(1)}h` : '—'}</TableCell>
                          <TableCell className="text-sm text-right">{wt.overtime_2_0 > 0 ? `${wt.overtime_2_0.toFixed(1)}h` : '—'}</TableCell>
                          <TableCell className="text-sm text-right font-semibold">{wt.total_hours.toFixed(1)}h</TableCell>
                          <TableCell>
                            <button
                              className="p-1 rounded hover:bg-muted/50 transition-colors"
                              onClick={(e) => { e.stopPropagation(); toggleWorkerExpand(wt.worker_id); }}
                              data-testid={`export-worker-expand-${i}`}
                            >
                              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          </TableCell>
                        </TableRow>
                        {/* Expanded day + block detail */}
                        {isExpanded && (
                          <>
                            {/* Export Lines table — only when per_block_tracking provides export_lines */}
                            {wt.export_lines && wt.export_lines.length > 0 && (
                              <TableRow key={`export-lines-header-${i}`} className="bg-indigo-500/5">
                                <TableCell></TableCell>
                                <TableCell colSpan={6} className="py-2">
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-500 mb-1">
                                      <Layers className="h-3 w-3" /> Export Lines
                                    </div>
                                    <table className="w-full text-[11px]" data-testid="export-lines-table">
                                      <thead>
                                        <tr className="border-b border-indigo-500/10">
                                          <th className="text-left font-medium py-0.5 text-muted-foreground">Rate</th>
                                          <th className="text-left font-medium py-0.5 text-muted-foreground">Tracking</th>
                                          <th className="text-left font-medium py-0.5 text-muted-foreground">Source</th>
                                          <th className="text-right font-medium py-0.5 text-muted-foreground">Hours</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(() => {
                                          const RATE_ORDER = { ordinary: 0, overtime_1_5: 1, overtime_2_0: 2, travel: 3 };
                                          const RATE_LABELS = { ordinary: 'Ordinary', overtime_1_5: 'OT 1.5', overtime_2_0: 'OT 2.0', travel: 'Travel' };
                                          const sorted = [...wt.export_lines].sort((a, b) =>
                                            (RATE_ORDER[a.rate_key] ?? 9) - (RATE_ORDER[b.rate_key] ?? 9)
                                            || (a.tracking_label || '').localeCompare(b.tracking_label || '')
                                          );
                                          return sorted.map((el, eli) => (
                                          <tr key={eli} className="border-b border-muted/10">
                                            <td className="py-0.5">{RATE_LABELS[el.rate_key] || el.rate_key}</td>
                                            <td className="py-0.5">{el.tracking_label || '(Default)'}</td>
                                            <td className="py-0.5">
                                              <Badge variant="outline" className={`text-[9px] ${
                                                el.tracking_source === 'block' ? 'text-blue-500 border-blue-400/30' :
                                                el.tracking_source === 'rule' ? 'text-green-500 border-green-400/30' :
                                                'text-muted-foreground border-muted'
                                              }`}>
                                                {el.tracking_source === 'block' ? 'Block' : el.tracking_source === 'rule' ? 'Rule' : 'Default'}
                                              </Badge>
                                            </td>
                                            <td className="py-0.5 text-right font-mono">{el.hours?.toFixed(1)}h</td>
                                          </tr>
                                          ));
                                        })()}
                                        <tr className="font-semibold">
                                          <td className="pt-1" colSpan={3}>Total</td>
                                          <td className="pt-1 text-right font-mono">
                                            {wt.export_lines.reduce((s, el) => s + (el.hours || 0), 0).toFixed(1)}h
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            )}
                            {workerDays.map((day, di) => (
                          <>
                            <TableRow key={`day-${i}-${di}`} className="bg-muted/10">
                              <TableCell></TableCell>
                              <TableCell colSpan={2} className="text-xs text-muted-foreground pl-6">
                                {day.date} &nbsp;
                                <span className="font-mono">{day.start_time}–{day.end_time}</span>
                              </TableCell>
                              <TableCell className="text-xs text-right text-muted-foreground">{day.ordinary.toFixed(1)}h</TableCell>
                              <TableCell className="text-xs text-right text-muted-foreground">{day.overtime_1_5 > 0 ? `${day.overtime_1_5.toFixed(1)}h` : ''}</TableCell>
                              <TableCell className="text-xs text-right text-muted-foreground">{day.overtime_2_0 > 0 ? `${day.overtime_2_0.toFixed(1)}h` : ''}</TableCell>
                              <TableCell className="text-xs text-right text-muted-foreground font-medium">{day.total_hours.toFixed(1)}h</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                            {(day.time_blocks || []).map((block, bi) => (
                              <TableRow key={`block-${i}-${di}-${bi}`} className="bg-muted/5">
                                <TableCell></TableCell>
                                <TableCell colSpan={2} className="text-[11px] pl-10">
                                  <div className="flex items-center gap-1.5">
                                    {block.is_unallocated ? (
                                      <Badge variant="outline" className="text-amber-500 border-amber-500/30 text-[9px]">Unalloc</Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-[9px]">{block.job_number ? `#${block.job_number}` : 'Job'}</Badge>
                                    )}
                                    {block.customer_name && <span className="text-muted-foreground truncate max-w-[100px]">{block.customer_name}</span>}
                                    <span className="font-mono text-muted-foreground">{block.start_time}–{block.end_time}</span>
                                  </div>
                                </TableCell>
                                <TableCell colSpan={3} className="text-[11px] text-right">
                                  <span className="font-mono">{(block.hours || 0).toFixed(1)}h</span>
                                  {/* Resolved tracking label + source badge (per_block_tracking) */}
                                  {block.resolved_tracking && (
                                    <>
                                      <span className="ml-1.5 text-[9px] text-indigo-400">
                                        {block.resolved_tracking.option_name || block.resolved_tracking.myob_job_uid || ''}
                                      </span>
                                      {block.tracking_source && (
                                        <Badge variant="outline" className={`ml-1 text-[8px] ${
                                          block.tracking_source === 'block' ? 'text-blue-500 border-blue-400/30' :
                                          block.tracking_source === 'rule' ? 'text-green-500 border-green-400/30' :
                                          'text-muted-foreground border-muted'
                                        }`}>
                                          {block.tracking_source === 'block' ? 'Block' : block.tracking_source === 'rule' ? 'Rule' : 'Default'}
                                        </Badge>
                                      )}
                                    </>
                                  )}
                                  {/* Legacy tracking display when no resolved_tracking */}
                                  {!block.resolved_tracking && (
                                    <>
                                      {block.tracking_rule_name && (
                                        <Badge variant="outline" className="ml-1.5 text-[9px] text-green-400 border-green-400/30">
                                          {block.tracking_rule_name}
                                        </Badge>
                                      )}
                                      {block.tracking?.xero?.length > 0 && (
                                        <span className="ml-1.5 text-[9px] text-indigo-400">
                                          {block.tracking.xero.map(t => t.option_name || t.option_id).join(', ')}
                                        </span>
                                      )}
                                      {block.tracking?.myob_job_uid && (
                                        <span className="ml-1.5 text-[9px] text-indigo-400">Job: {block.tracking.myob_job_uid}</span>
                                      )}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            ))}
                          </>
                        ))}
                          </>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Export button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExport}
                disabled={exporting || selectedCount === 0 || preview.warnings?.some(w => w.includes('not mapped'))}
                className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                data-testid={`${platform}-export-btn`}
              >
                {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Export {selectedCount} {selectedCount === 1 ? 'Worker' : 'Workers'} to {platformName}
              </Button>
              {selectedCount === 0 && (
                <span className="text-xs text-muted-foreground">Select at least one worker</span>
              )}
            </div>
          </div>
        )}

        {preview && preview.worker_totals?.length === 0 && (
          <div className="text-sm text-muted-foreground p-4 border rounded-md text-center">
            No approved timesheets found for this period with mapped workers.
          </div>
        )}

        {/* Export result */}
        {exportResult && (
          <div className={`p-3 rounded-lg border ${exportResult.failed?.length > 0 ? 'border-amber-500/30 bg-amber-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
            <div className="flex items-center gap-2 mb-2">
              {exportResult.failed?.length > 0 ? <AlertTriangle className="h-4 w-4 text-amber-400" /> : <Check className="h-4 w-4 text-green-400" />}
              <span className="text-sm font-medium">{exportResult.message}</span>
            </div>
            {exportResult.success?.length > 0 && (
              <div className="text-xs text-muted-foreground space-y-0.5">
                {exportResult.success.map((s, i) => (
                  <div key={i} className="flex items-center gap-1"><Check className="h-3 w-3 text-green-400" /> {s.worker_name}: {s.hours?.toFixed(1)}h</div>
                ))}
              </div>
            )}
            {exportResult.failed?.length > 0 && (
              <div className="text-xs text-destructive space-y-0.5 mt-2">
                {exportResult.failed.map((f, i) => (
                  <div key={i} className="flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {f.worker_name}: {f.error}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History toggle */}
        <div className="pt-2">
          <Button variant="ghost" size="sm" className="text-xs" onClick={showHistory ? () => setShowHistory(false) : loadHistory} disabled={historyLoading}>
            {historyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : showHistory ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
            {showHistory ? 'Hide' : 'Show'} Export History
          </Button>

          {showHistory && history.length > 0 && (
            <div className="mt-2 space-y-1">
              {history.filter(h => h.platform === platform).map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs p-2 rounded bg-muted/20">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span>{h.period_start} — {h.period_end}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {h.success_count > 0 && <Badge variant="outline" className="text-green-400 border-green-400/30 text-[10px]">{h.success_count} ok</Badge>}
                    {h.failed_count > 0 && <Badge variant="outline" className="text-red-400 border-red-400/30 text-[10px]">{h.failed_count} failed</Badge>}
                    <span className="text-muted-foreground">{h.created_at ? format(new Date(h.created_at), 'dd MMM HH:mm') : ''}</span>
                  </div>
                </div>
              ))}
              {history.filter(h => h.platform === platform).length === 0 && (
                <div className="text-xs text-muted-foreground p-2">No exports yet for {platformName}</div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
